import { Injectable, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

import { PaginatedItems, snapshot, UserService, ArmyBuilderConfig } from '../../../global';

@Injectable({ providedIn: 'root' })
export class AdminEntityService {
    private entityListUrl = `${this.config.apiBaseUrl}/library/admin`;
    private entities$ = new BehaviorSubject<{ [id: string]: any }>({});
    env = signal(null);

    constructor(
        private userService: UserService,
        private config: ArmyBuilderConfig
    ) {
        this.entities$.subscribe((e) => console.log({ e }));
        this.getEnvironment().subscribe((env) => {
            console.log('!env', env);
            this.env.set(env);
        });
    }

    saveEntity(entity: any, gameId: string, entityType: string, promote = false) {
        console.log('Saving entity...', entity);
        let url = `${this.entityListUrl}/${gameId}/${entityType}`;
        if (promote) {
            url = `${this.config.apiBaseUrl}/admin/promotion/entity/${gameId}/${entityType}/promoteToProd`;
        }
        return this.userService.adminOnlyRoute('post', url, entity).pipe(
            tap((_) => {
                snapshot(this.entities$, (entities) => {
                    entities[entity._id] = entity;
                    this.entities$.next(entities);
                });
            })
        );
    }

    saveMany(entities: any[], gameId: string, entityType: string) {
        console.log('Saving multiple entities...', entities);
        const url = `${this.entityListUrl}/${gameId}/${entityType}`;
        return this.userService.adminOnlyRoute('post', url, entities);
        // .pipe
        // tap((_) => {
        //     snapshot(this.entities$, (entities) => {
        //         entities[entity._id] = entity;
        //         this.entities$.next(entities);
        //     });
        // })
        // ();
    }

    deleteEntity(entity: any, gameId: string, entityType: string) {
        console.log('Deleting entity...', entity);
        const url = `${this.entityListUrl}/${gameId}/${entityType}/${entity._id}`;
        return this.userService.adminOnlyRoute('delete', url);
    }

    /** Updates only image fields on an entity. Works for admin and image-editor roles. Supports both flat (legacy) and nested (image.id) structures. */
    saveEntityImageFields(entity: { _id: string; [key: string]: any }, gameId: string, entityType: string) {
        const url = `${this.config.apiBaseUrl}/library/image/${gameId}/${entityType}/${entity._id}`;

        // Support both nested (image.id) and flat (imageId) structures
        // Determine which structure is being used
        const hasNestedImage = entity.image && typeof entity.image === 'object';

        let imageFields: any;
        if (hasNestedImage) {
            // New nested structure
            imageFields = {
                image: {
                    id: entity.image?.id ?? null,
                    sourceId: entity.image?.sourceId ?? null,
                    sourceFileName: entity.image?.sourceFileName ?? null,
                    zoom: entity.image?.zoom ?? null,
                    transform: entity.image?.transform ?? null,
                    cropWidth: entity.image?.cropWidth ?? null,
                    cropHeight: entity.image?.cropHeight ?? null,
                    cropX: entity.image?.cropX ?? null,
                    cropY: entity.image?.cropY ?? null
                }
            };
        } else {
            // Legacy flat structure (for backward compatibility during migration)
            imageFields = {
                imageId: entity.imageId ?? null,
                imageSourceId: entity.imageSourceId ?? null,
                imageSourceFileName: entity.imageSourceFileName ?? null,
                imageZoom: entity.imageZoom ?? null,
                imageTransform: entity.imageTransform ?? null,
                imageCropWidth: entity.imageCropWidth ?? null,
                imageCropHeight: entity.imageCropHeight ?? null,
                imageCropX: entity.imageCropX ?? null,
                imageCropY: entity.imageCropY ?? null
            };
        }

        return this.userService.authenticatedPatch(url, imageFields).pipe(
            tap(() => {
                snapshot(this.entities$, (entities) => {
                    entities[entity._id] = { ...entities[entity._id], ...imageFields };
                    this.entities$.next(entities);
                });
            })
        );
    }

    saveCardScaleFields(
        entity: { _id: string; unitCard?: { cardTextScale?: number; cardImageScale?: number; cardBackTextScale?: number } },
        gameId: string,
        entityType: string
    ) {
        const url = `${this.config.apiBaseUrl}/library/card-scale/${gameId}/${entityType}/${entity._id}`;
        const fields = {
            cardTextScale: entity.unitCard?.cardTextScale ?? null,
            cardImageScale: entity.unitCard?.cardImageScale ?? null,
            cardBackTextScale: entity.unitCard?.cardBackTextScale ?? null
        };
        return this.userService.authenticatedPatch(url, fields).pipe(
            tap(() => {
                snapshot(this.entities$, (entities) => {
                    entities[entity._id] = {
                        ...entities[entity._id],
                        unitCard: entity.unitCard
                    };
                    this.entities$.next(entities);
                });
            })
        );
    }

    saveBatchCardScaleFields(
        entities: Array<{ _id: string; unitCard?: { cardTextScale?: number; cardImageScale?: number; cardBackTextScale?: number } }>,
        gameId: string,
        entityType: string
    ) {
        const url = `${this.config.apiBaseUrl}/library/card-scale/${gameId}/${entityType}`;
        const payload = entities.map((entity) => ({
            _id: entity._id,
            cardTextScale: entity.unitCard?.cardTextScale ?? null,
            cardImageScale: entity.unitCard?.cardImageScale ?? null,
            cardBackTextScale: entity.unitCard?.cardBackTextScale ?? null
        }));
        return this.userService.adminOnlyRoute('post', url, payload).pipe(
            tap(() => {
                snapshot(this.entities$, (entities_map) => {
                    for (const entity of entities) {
                        entities_map[entity._id] = {
                            ...entities_map[entity._id],
                            unitCard: entity.unitCard
                        };
                    }
                    this.entities$.next(entities_map);
                });
            })
        );
    }

    getEntities(gameId: string, entityType: string, skip: number, query: string = '', sort: string = '', pageSize = 10) {
        const qs = (key, val) => (val ? `&${key}=${val}` : '');
        const url = `${this.entityListUrl}/${gameId}/${entityType}?pageSize=${pageSize}&skip=${skip}${qs('query', query)}${qs(
            'sort',
            sort || 'name.en|asc'
        )}`;
        return this.userService.adminOnlyRoute('get', url).pipe(map((data) => data as PaginatedItems));
    }

    getStagingOnlyEntities(gameId: string, entityType: string, skip: number, sort: string = '', pageSize = 10) {
        const qs = (key, val) => (val ? `&${key}=${val}` : '');
        const url = `${this.config.apiBaseUrl}/admin/promotion/stagingOnly/${gameId}/${entityType}?pageSize=${pageSize}&skip=${skip}${qs('sort', sort || 'name.en|asc')}`;
        return this.userService.adminOnlyRoute('get', url).pipe(map((data) => data as PaginatedItems));
    }

    promoteStagingOnlyEntities(gameId: string, entityType: string) {
        const url = `${this.config.apiBaseUrl}/admin/promotion/stagingOnly/${gameId}/${entityType}`;
        return this.userService.adminOnlyRoute('post', url);
    }

    getStatSummary(stat: string) {
        const url = `${this.config.apiBaseUrl}/admin/promotion/stats/${stat}/summary`;
        return this.userService.adminOnlyRoute('get', url);
    }

    getEntitiesByStat(stat: string, gameId: string, entityType: string, skip: number, sort: string = '', pageSize = 10, diff = false) {
        const qs = (key, val) => (val ? `&${key}=${val}` : '');
        const url = `${this.config.apiBaseUrl}/admin/promotion/stats/${stat}/${gameId}/${entityType}?pageSize=${pageSize}&skip=${skip}${qs('sort', sort || 'name.en|asc')}${diff ? '&diff=true' : ''}`;
        return this.userService.adminOnlyRoute('get', url).pipe(map((data) => data as PaginatedItems));
    }

    executeStatAction(stat: string, gameId: string, entityType: string) {
        const url = `${this.config.apiBaseUrl}/admin/promotion/stats/${stat}/${gameId}/${entityType}`;
        return this.userService.adminOnlyRoute('post', url);
    }

    executeStatActionForGame(stat: string, gameId: string) {
        const url = `${this.config.apiBaseUrl}/admin/promotion/stats/${stat}/${gameId}`;
        return this.userService.adminOnlyRoute('post', url);
    }

    getEntityByRoute(route: ActivatedRoute) {
        return route.params.pipe(
            map((p) => ({ entityId: p.entityId, entityType: p.entityType, gameId: p.gameId })),
            filter((p) => p?.gameId && p?.entityType && p?.entityId),
            switchMap((x) => this.getEntity(x.gameId, x.entityType, x.entityId))
        );
    }

    getEntity(gameId: string, entityType: string, entityId: string) {
        const url = `${this.entityListUrl}/${gameId}/${entityType}/${entityId}`;
        return this.userService
            .adminOnlyRoute('get', url)
            .pipe(map((data) => data as any))
            .pipe(
                tap((entity) => {
                    snapshot(this.entities$, (entities) => {
                        entities[entity._id] = entity;
                        this.entities$.next(entities);
                    });
                }),
                switchMap((entity) => {
                    return this.entities$.pipe(map((entities) => entities[entity._id]));
                })
            );
    }

    getEnvironment() {
        const url = `${this.config.apiBaseUrl}/admin/promotion/environment`;
        return this.userService.adminOnlyRoute('get', url).pipe(map((data) => data?.environment as any));
    }
}
